<template>
  <svg
    class="progress-ring"
    viewBox="0 0 200 200"
    :style="{ width: size, height: size }"
    role="img"
    aria-label="Progress"
  >
    <circle
      class="ring-track"
      cx="100"
      cy="100"
      :r="radius"
      fill="none"
      :stroke="trackColor"
      :stroke-width="strokeWidth"
    />
    <circle
      class="ring-fill"
      cx="100"
      cy="100"
      :r="radius"
      fill="none"
      :stroke="fillColor"
      :stroke-width="strokeWidth"
      :stroke-dasharray="dashArray"
      :stroke-dashoffset="dashOffset"
      stroke-linecap="round"
      :style="fillStyle"
    />
  </svg>
</template>

<script setup lang="ts">
import { computed } from 'vue'

// Pure presentational circular progress ring. Geometry mirrors ProgressDonut
// (proven start-at-top, clockwise fill) but with no center label. Shared by the
// IconList progress-sequence markers (rAF-driven, no transition) and the
// registered ProgressRing component (value-driven, transitionMs > 0 to smooth
// discrete changes).
const props = withDefaults(
  defineProps<{
    progress: number // 0..100
    size: string
    strokeWidth: number // SVG viewBox units (0..200)
    trackColor: string
    fillColor: string
    transitionMs?: number // smooth the dash on value change; 0 = none
  }>(),
  { progress: 0, size: '32px', strokeWidth: 16, transitionMs: 0 },
)

const clamped = computed(() => Math.min(100, Math.max(0, Number(props.progress) || 0)))
const radius = computed(() => (200 - props.strokeWidth) / 2)
const circumference = computed(() => radius.value * 2 * Math.PI)
const dashOffset = computed(() => circumference.value / 4)
const dashArray = computed(() => {
  const fill = (clamped.value / 100) * circumference.value
  return `${fill} ${circumference.value - fill}`
})
const fillStyle = computed(() =>
  props.transitionMs > 0 ? { transition: `stroke-dasharray ${props.transitionMs}ms linear` } : {},
)
</script>

<style scoped lang="scss">
.progress-ring {
  display: block;
  flex-shrink: 0;
}
</style>
